import { Button, Checkbox, Form, Input, Radio, Select } from "antd";
import { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import { useNavigate, useParams } from "react-router-dom";
import AddIcon from "../../../assets/icons/AddIcon";
import CircleCrossIcon from "../../../assets/icons/CircleCrossIcon";
import PdfIcon from "../../../assets/icons/PdfIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import VideoIcon from "../../../assets/icons/VideoIcon";
import pdfLogo from "../../../assets/images/Rectangle 4022.png";
import { IMAGE_URL } from "../../../constants/constants";
import useModal from "../../../hooks/useModal";
import AdminLayout from "../../../layouts/AdminLayout";
import { formats } from "../../../lib/TextEditor/Formats";
import { modules } from "../../../lib/TextEditor/Modules";
import { toast } from "../../../lib/toast";
import { getAllCategoryAsync } from "../../../store/features/category/categoryApi";
import {
  createTrainingMaterialAsync,
  singTrainingMaterialAsync,
  updateTrainingMaterialAsync,
} from "../../../store/features/trainingMaterial/trainingMaterialApi";
import { useAppDispatch, useAppSelector } from "../../../store/store";
import { ArrayOption, selectSearchOption } from "../../../utils/utils";
import CustomSection from "../../ui/CustomSection";
import { DimensionOfImage } from "../../ui/DimensionOfImage";
import FileUpload from "../../ui/FileUpload";
import IconicTitle from "../../ui/IconicTitle";

export default function AddTrainingMaterial() {
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const [closePdf, setClosePdf] = useState("");
  const { open, handleOpen, toggle } = useModal();
  const [form] = Form.useForm();
  const [checkAll, setCheckAll] = useState(false);
  const [videoLinks, setVideoLinks] = useState<string[]>([""]);
  const [pdfFiles, setPdfFiles] = useState<string[]>([]);
  const { singleTrainingMaterialData } = useAppSelector(
    (state) => state.trainingMaterial
  );
  const navigate = useNavigate();
  const { CategoryList } = useAppSelector((state) => state.category);
  const [content, setContent] = useState("");

  const { login } = useAppSelector((state) => state.login);

  const statusList = ["Published", "CSO-Admin-Approved", "Draft", "Archived"];

  const filteredStatusList =
    login?.role_id?.role_type === "cso_admin"
      ? statusList.filter((item) => item !== "Published")
      : statusList.filter((item) => item !== "CSO-Admin-Approved");

  // Fetch single training material data if ID exists
  useEffect(() => {
    if (id) {
      dispatch(singTrainingMaterialAsync({ params: { id } }));
    }
  }, [id, dispatch]);

  // Populate form when single data is loaded
  useEffect(() => {
    if (id && singleTrainingMaterialData) {
      form.setFieldsValue({
        category: singleTrainingMaterialData.categoryId._id,
        title: singleTrainingMaterialData.trainingMaterialTitle,
        image: singleTrainingMaterialData.trainingMaterialThumbnail,
        status: singleTrainingMaterialData.trainingMaterialStatus || "Draft",
        content: singleTrainingMaterialData.trainingMaterialContent,
        trainingMaterialDownloadAccess:
          singleTrainingMaterialData.trainingMaterialDownloadAccess
            ? "Yes"
            : "No",
      });

      // Update content state
      setContent(singleTrainingMaterialData.trainingMaterialContent || "");
      setPdfFiles(singleTrainingMaterialData.trainingMaterialPdfFiles || []);

      if (singleTrainingMaterialData.trainingMaterialVideoLinks?.length) {
        setVideoLinks(singleTrainingMaterialData.trainingMaterialVideoLinks);
        singleTrainingMaterialData.trainingMaterialVideoLinks.forEach(
          (link, index) => {
            form.setFieldValue(`videoLink${index}`, link);
          }
        );
      }
    }
  }, [singleTrainingMaterialData, form, id]);

  // Handle form submission
  const onFinish = (values: any) => {
    const data = {
      trainingMaterialTitle: values.title,
      categoryId: values.category,
      trainingMaterialThumbnail: values.image,
      trainingMaterialPdfFiles: pdfFiles,
      trainingMaterialVideoLinks: videoLinks.filter((link) => link !== ""),
      trainingMaterialStatus: values.status || "Draft",
      trainingMaterialContent: content,
      trainingMaterialDownloadAccess:
        values.trainingMaterialDownloadAccess === "Yes",
    };

    if (id) {
      dispatch(
        updateTrainingMaterialAsync({
          data,
          others: {
            id,
            reRender: () => {
              toast("success", "Training material updated successfully");
              form.resetFields();
              setPdfFiles([]);
              setVideoLinks([""]);
              navigate("/admin/training/training-material");
            },
          },
        })
      );
    } else {
      dispatch(
        createTrainingMaterialAsync({
          data,
          others: {
            reRender: () => {
              toast("success", "Training material created successfully");
              form.resetFields();
              setPdfFiles([]);
              setVideoLinks([""]);
              navigate("/admin/training/training-material");
            },
          },
        })
      );
    }
  };

  // Add more video link input
  const addVideoLink = () => {
    setVideoLinks([...videoLinks, ""]);
  };

  // Update video link at index
  const updateVideoLink = (index: number, value: string) => {
    const newLinks = [...videoLinks];
    newLinks[index] = value;
    setVideoLinks(newLinks);
  };

  const handleCheckAll = (e: any) => {
    const checked = e.target.checked;
    setCheckAll(checked);
  };

  useEffect(() => {
    dispatch(
      getAllCategoryAsync({
        params: {
          skip: 0,
          limit: 0,
          categoryType: "Training-Material",
          categoryStatus: "Published",
        },
      })
    );
  }, [dispatch]);

  const hasRequiredAttachments = () => {
    const hasValidPdf = pdfFiles.length > 0;
    const hasValidVideo = videoLinks.some((link) => link.trim() !== "");
    return hasValidPdf || hasValidVideo;
  };

  return (
    <AdminLayout
      title={`${id ? "Update" : "Create new"} training material`}
      backLink="/admin/training/training-material"
    >
      <Form
        className="custom-form space-y-[14px]"
        layout="vertical"
        onFinish={onFinish}
        size="large"
        form={form}
      >
        <CustomSection>
          <div className="grid grid-cols-2 gap-x-2.5 w-full">
            <Form.Item
              name="category"
              label="Select Category"
              rules={[
                {
                  required: true,
                  message: "Please select category",
                },
              ]}
            >
              <Select
                placeholder="Select"
                size="large"
                allowClear
                showSearch
                filterOption={selectSearchOption}
                options={ArrayOption(CategoryList, "categoryName", "_id")}
                suffixIcon={<SelectDownIcon />}
                disabled={open?.type === "VIEW"}
              />
            </Form.Item>
            <Form.Item
              name="image"
              label="Choose a Thumbnail"
              valuePropName="fileUrl"
              extra={DimensionOfImage({ width: 1032, height: 435 })}
              rules={[
                {
                  required: true,
                  message: "Please upload thumbnail",
                },
              ]}
            >
              <FileUpload
                form={form}
                accept={acceptFile}
                name="image"
                onChange={(url) =>
                  form.setFieldsValue({ image: url || undefined })
                }
                fileType={open?.type === "EDIT"}
              />
            </Form.Item>

            <Form.Item
              className="col-span-2"
              name="title"
              label="Post Title"
              rules={[
                {
                  required: true,
                  message: "Please enter title",
                },
              ]}
            >
              <Input placeholder="Enter title here" size="large" />
            </Form.Item>
          </div>

          <Form.Item
            className="col-span-2"
            name="content"
            initialValue=""
            rules={[
              {
                required: true,
                message: "Please enter content",
              },
            ]}
          >
            <ReactQuill
              className="h-[372px]"
              theme="snow"
              formats={formats}
              modules={modules}
              value={content}
              onChange={(value) => {
                setContent(value);
                form.setFieldsValue({ content: value });
              }}
            />
            <br />
            <br />
          </Form.Item>
        </CustomSection>

        <CustomSection>
          <IconicTitle
            icon={<PdfIcon />}
            className="text-primary"
            title="PDF File Attachment"
          />

          <div className="grid sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-9 gap-6 mt-6">
            {pdfFiles.map((file, index) => (
              <div key={index} className="relative mb-1">
                <a
                  href={`${IMAGE_URL}${file}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cursor-pointer"
                >
                  <img
                    className="w-[105px] h-[124.50px] rounded-[3px] border border-[#e9eff6] border-solid"
                    src={pdfLogo}
                    alt="PDF"
                  />
                  <div className="text-body text-sm font-medium leading-tight">
                    {file?.split("/").pop()}
                  </div>
                </a>
                <div
                  className="w-4 h-4 left-[83px] top-[6px] absolute cursor-pointer"
                  onClick={() => {
                    const newFiles = [...pdfFiles];
                    newFiles.splice(index, 1);
                    setPdfFiles(newFiles);
                    setClosePdf(file);
                  }}
                >
                  <CircleCrossIcon />
                </div>
              </div>
            ))}
          </div>

          <Form.Item name="pdfFile" valuePropName="fileUrl">
            <FileUpload
              form={form}
              accept={acceptPdfFile}
              name="pdf"
              onChange={(url: any) => {
                if (url?.endpoint) {
                  setPdfFiles([...pdfFiles, url.endpoint]);
                  form.setFieldValue("pdfFile", undefined);
                } else if (typeof url === "string") {
                  setPdfFiles([...pdfFiles, url]);
                  form.setFieldValue("pdfFile", undefined);
                }
              }}
              removeFile={closePdf}
            />
          </Form.Item>
        </CustomSection>

        <CustomSection>
          <IconicTitle
            icon={<VideoIcon />}
            className="text-primary"
            title="Video Link Attachment"
          />

          {videoLinks.map((link, index) => (
            <div key={index} className="relative">
              <Form.Item
                className="col-span-2 mt-6 pr-10"
                name={`videoLink${index}`}
                label={`Video Link ${index + 1}`}
                rules={[
                  {
                    validator: async (_, value) => {
                      if (!videoLinks.some((link) => link.trim() !== "")) {
                        throw new Error("Please add at least one video link");
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  placeholder="Enter video URL"
                  prefix="http://"
                  size="large"
                  value={link}
                  onChange={(e) => updateVideoLink(index, e.target.value)}
                />
              </Form.Item>

              {videoLinks.length > 1 && (
                <div
                  className="absolute right-0 top-14 cursor-pointer"
                  onClick={() => {
                    const newLinks = [...videoLinks];
                    newLinks.splice(index, 1);
                    setVideoLinks(newLinks);
                    form.setFieldValue(`videoLink${index}`, undefined);
                  }}
                >
                  <CircleCrossIcon />
                </div>
              )}
            </div>
          ))}

          <Button type="dashed" onClick={addVideoLink} icon={<AddIcon />}>
            Add More Video Links
          </Button>
        </CustomSection>

        <CustomSection>
          <IconicTitle
            icon={
              <>
                <PdfIcon /> <VideoIcon />
              </>
            }
            className="text-primary"
            title="Download Access"
          />
          <div className="flex items-center gap-x-2.5 mb-0 mt-2">
            <Form.Item name="trainingMaterialDownloadAccess" initialValue="Yes">
              <Radio.Group
                disabled={open?.type === "VIEW"}
                className="custom-radio"
                size="large"
              >
                {["Yes", "No"].map((el, idx) => (
                  <Radio
                    className="text-body text-base font-medium leading-7 mr-3.5"
                    value={el}
                    key={idx}
                  >
                    {el}
                  </Radio>
                ))}
              </Radio.Group>
            </Form.Item>
          </div>
        </CustomSection>

        <CustomSection>
          <div className="flex items-center gap-x-2.5 mb-4">
            <Form.Item name="status" initialValue="Draft">
              <Radio.Group
                disabled={open?.type === "VIEW"}
                className="custom-radio"
                size="large"
              >
                {filteredStatusList.map((el, idx) => (
                  <Radio
                    className="text-body text-base font-medium leading-7 mr-3.5"
                    value={el}
                    key={idx}
                  >
                    {el}
                  </Radio>
                ))}
              </Radio.Group>
            </Form.Item>
          </div>

          <div className="border border-solid border-[#d7dee8] mt-1 mb-4 w-full" />

          <div className="flex justify-between items-center">
            <Checkbox checked={checkAll} onChange={handleCheckAll}>
              <span className="text-body text-sm font-medium leading-normal">
                Yes! I confirm and want to publish this material
              </span>
            </Checkbox>
            <div className="flex gap-x-[12px]">
              <Button
                className="cancel-btn"
                type="text"
                size="large"
                // onClick={() =>
                //   handleOpen({
                //     type: "EDIT",
                //   })
                // }
              >
                Cancel
              </Button>

              <Button
                className="primary_btn"
                type="primary"
                htmlType="submit"
                size="large"
                disabled={!checkAll || !hasRequiredAttachments()}
              >
                {id ? "Update" : "Save"}
              </Button>
            </div>
          </div>
        </CustomSection>
      </Form>
    </AdminLayout>
  );
}

const statusData = [
  {
    _id: 1,
    name: "Category 1",
  },
  {
    _id: 2,
    name: "Category 2",
  },
];
const acceptFile = ".jpg, .jpeg, .png";
const acceptPdfFile = ".pdf";
